import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useAuth } from "@/features/auth/use-auth";
import { useFirebase } from "@/hooks/use-firebase";
import { getGroupWithMember, listUserGroups } from "@/services/group-service";
import { listPages, listPagesDirect } from "@/services/page-service";
import type { GroupRole } from "@/types/domain";

export const groupKeys = {
  all: (userId: string) => ["groups", userId] as const,
  detail: (groupId: string, userId: string) =>
    ["groups", groupId, userId] as const,
  pages: (groupId: string) => ["groups", groupId, "pages"] as const,
};

export function useGroups() {
  const { user } = useAuth();
  const { db } = useFirebase();
  return useQuery({
    queryKey: groupKeys.all(user?.uid ?? ""),
    queryFn: () => listUserGroups(db, user!.uid),
    enabled: Boolean(user),
  });
}

export function useCurrentGroup() {
  const { groupId = "" } = useParams();
  const { user } = useAuth();
  const { db } = useFirebase();
  const query = useQuery({
    queryKey: groupKeys.detail(groupId, user?.uid ?? ""),
    queryFn: () => getGroupWithMember(db, groupId, user!.uid),
    enabled: Boolean(groupId && user),
  });
  return { groupId, ...query };
}

export function useGroupPages(groupId: string, role?: GroupRole) {
  const { db, functions } = useFirebase();
  return useQuery({
    queryKey: groupKeys.pages(groupId),
    queryFn: async () => {
      try {
        return await listPages(functions, groupId);
      } catch (error) {
        if (role === "owner") {
          return listPagesDirect(db, groupId);
        }
        throw error;
      }
    },
    enabled: Boolean(groupId && role),
  });
}
